import { useDirection, useProperty, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { breakpoints, Card, Inline, Text, Notice, textVariants, Stack, vars } from '@etg/wings';
import { constants } from '@eti/utils';
import { useRef } from 'react';
import { useLinksAccessibility } from '../../../custom-hooks/useLinkAccessibility';
import { isCarrierCodesPresentInSomeSegments } from '../../../common/utils/carriersUtils';
import PersuasionModalProvider from '../../../common/context/PersuasionModalContext';
import getTranslatedTexts from '../../../common/text-resolvers/utils/getTranslatedTexts';
import { generateReadMore } from '../../../common/utils/readMore';
import { CABIN_BAGGAGE } from '../../../products';
import { bundleContentDetail, cabinBagDetails, cabinBaggageDescription, getTravelersPrimaryFlow } from '../utils/cabinBaggageUtils';
import { hasMultipleNonInfantTravelers } from '../../common/utils/baggageUtils';
import BaggageSectionHeader from '../../common/components/BaggageSectionHeader';
import BaggageSectionDescription from '../../common/components/BaggageSectionDescription';
import BaggageIllustration from '../../common/components/BaggageIllustration';
import BaggageStatus from '../../common/components/BaggageStatus';
import ApplySelectionToAllCheckbox from '../../common/components/ApplySelectionToAllCheckbox';
import BaggageSelection from '../../common/components/BaggageSelection';
import useBaggageVisibilityTracker from '../../common/hooks/useBaggageVisibilityTracker';
import useSortedBaggageOptions from '../../common/hooks/useSortedBaggageOptions';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const generalTexts = {
  descriptionLabel: 'Product.CabinBaggagePerTraveler.Description.Label',
  includedBaggageStatusLabel: 'TravelerDetails.Status.Included',
  notAvailableCabinBaggageText: 'Product.CabbinBaggagePerTraveler.Not.Available.To.Offer.Text',
  notAvailableCabinBaggageBadgeStatusText: 'TravelerDetails.Status.Unavailable',
  offeringText: 'Product.CabinBaggage.Integrated.Offering.Text',
  persuasionContent: 'cabinBaggagePerTravelerPersuasionContent',
  readMoreContent: 'cabinBaggagePerTravelerReadMoreModalContent',
  sameChoiceForAllLabel: 'Product.CabinBaggage.SelectSameChoiceForAll',
  title: 'cabinBaggagePerTravelerTitle',
  titleChoiceNo: 'cabinBaggagePerTravelerConfirmationNo'
};
const productName = CABIN_BAGGAGE.name;
const cabinBaggageCardStyles = css`
  border: 1px solid ${vars.colors.divider};
  margin-block-end: 16px;
`;
const baggageSelectionStyles = css`
  gap: 8px;
  margin-top: 2px;
  width: 100%;

  @media (min-width: ${breakpoints._768}) {
    gap: 16px;
    width: 85%;
  }
`;
const notAvailableBaggageStatusTextStyle = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const carrierTextStyles = css`
  max-width: 85%;
`;
const CabinBaggagePrimaryFlow = ({
  bundleData,
  firstTravelerSelectionValue,
  totalBaggageQuantity,
  includedBaggage,
  isBaggageAvailableOrIncluded,
  order,
  productPosition,
  readMore = {
    isEnabled: true
  },
  shouldApplySelectionToAll,
  onChangeAll,
  onChange,
  shouldUseCarrierSpecificUnavailableLabel,
  travelerNumber
}) => {
  var _traveler$ancillaryCa, _traveler$markup, _generateReadMore;
  const {
    t
  } = useTranslation();
  const {
    p
  } = useProperty();
  const {
    isRTL
  } = useDirection();
  const rootElement = useRef(null);
  const isCabinBaggageBundle = bundleContentDetail(bundleData);
  const includedCabinBaggageInTrip = includedBaggage || isCabinBaggageBundle;
  const isCabinBaggageIncludedInTrip = includedCabinBaggageInTrip && Object.keys(includedCabinBaggageInTrip).length !== 0;
  const availableForPurchase = isBaggageAvailableOrIncluded && !isCabinBaggageIncludedInTrip;
  const notAvailableForPurchase = !isBaggageAvailableOrIncluded && !isCabinBaggageIncludedInTrip;
  useBaggageVisibilityTracker({
    isBaggageAvailableOrIncluded,
    includedBaggageInTrip: isCabinBaggageIncludedInTrip,
    productName,
    rootElement,
    productPosition
  });
  const {
    descriptionLabel,
    includedBaggageStatusLabel,
    notAvailableCabinBaggageBadgeStatusText,
    notAvailableCabinBaggageText,
    offeringText,
    persuasionContent,
    readMoreContent,
    sameChoiceForAllLabel,
    title,
    titleChoiceNo
  } = getTranslatedTexts(generalTexts, t);
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: title
  });
  const ryanairCarrierCodes = p('IbeClient.RyanairMarketingCarriers') || [];
  const isRyanairInTrip = isCarrierCodesPresentInSomeSegments(ryanairCarrierCodes, order.bounds);
  const {
    pieces,
    dimensions,
    weight,
    weightUnit
  } = cabinBagDetails({
    bundleData,
    sellSpecification: order.sellSpecification,
    includedBaggage
  });
  const cabinBaggagePiecesWeightSizeDescription = cabinBaggageDescription({
    pieces,
    weight,
    weightUnit,
    dimensions,
    t
  });
  const travelers = getTravelersPrimaryFlow(order);
  const traveler = travelers[travelerNumber] || {};
  const isInfant = traveler.ageType === constants.ageTypes.INFANT;
  const isFirstTraveler = Number(traveler.id) === 0;
  const baggageOptions = [{
    ancillaryCarrierPriceBreakdowns: [],
    id: `content-false-${travelerNumber}`,
    label: titleChoiceNo !== null && titleChoiceNo !== void 0 ? titleChoiceNo : null,
    priceRaw: 0,
    markup: 0,
    value: 'false'
  }, {
    ancillaryCarrierPriceBreakdowns: (_traveler$ancillaryCa = traveler.ancillaryCarrierPriceBreakdowns) !== null && _traveler$ancillaryCa !== void 0 ? _traveler$ancillaryCa : [],
    id: `content-true-${travelerNumber}`,
    label: cabinBaggagePiecesWeightSizeDescription,
    priceRaw: traveler === null || traveler === void 0 ? void 0 : traveler.price,
    markup: (_traveler$markup = traveler === null || traveler === void 0 ? void 0 : traveler.markup) !== null && _traveler$markup !== void 0 ? _traveler$markup : 0,
    value: 'true'
  }];
  const sortedBaggageOptions = useSortedBaggageOptions(order.bounds, baggageOptions);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: !isInfant && /*#__PURE__*/_jsx("div", {
      ref: rootElement,
      children: /*#__PURE__*/_jsx(Card, {
        className: cx('travelerDetailsSection', cabinBaggageCardStyles),
        "data-testid": `travelerDetails-${productName}-card-${traveler.id}`,
        children: /*#__PURE__*/_jsxs(Stack, {
          "data-testid": `travelerDetails-productSection-product-${productName}-body-${traveler.id}`,
          children: [/*#__PURE__*/_jsxs(Stack, {
            spacing: 8,
            children: [/*#__PURE__*/_jsx(BaggageSectionHeader, {
              productName: productName,
              readMoreModal: (_generateReadMore = generateReadMore(readMore, readMoreContent)) !== null && _generateReadMore !== void 0 ? _generateReadMore : undefined,
              title: title,
              travelerId: traveler.id
            }), /*#__PURE__*/_jsx(BaggageSectionDescription, {
              description: descriptionLabel,
              productName: productName,
              travelerId: traveler.id
            })]
          }), /*#__PURE__*/_jsxs(Inline, {
            noWrap: true,
            children: [/*#__PURE__*/_jsx(BaggageIllustration, {
              isRTL: isRTL,
              productName: productName
            }), notAvailableForPurchase && /*#__PURE__*/_jsx(BaggageStatus, {
              className: notAvailableBaggageStatusTextStyle,
              dataTestId: "notAvailableBaggage-status",
              description: shouldUseCarrierSpecificUnavailableLabel ? notAvailableCabinBaggageText : '',
              statusLabel: notAvailableCabinBaggageBadgeStatusText,
              useNotice: true,
              variant: "caution"
            }), isCabinBaggageIncludedInTrip && /*#__PURE__*/_jsx(BaggageStatus, {
              dataOfferRate: CABIN_BAGGAGE.id,
              dataTestId: "includedBaggage-status",
              description: cabinBaggagePiecesWeightSizeDescription,
              statusLabel: includedBaggageStatusLabel,
              variant: "positive"
            }), availableForPurchase && /*#__PURE__*/_jsx(PersuasionModalProvider, {
              children: /*#__PURE__*/_jsxs(Stack, {
                className: baggageSelectionStyles,
                children: [/*#__PURE__*/_jsx(BaggageSelection, {
                  baggageOptions: sortedBaggageOptions,
                  firstTravelerSelectionValue: firstTravelerSelectionValue,
                  onChange: onChange,
                  order: order,
                  persuasionContent: persuasionContent,
                  persuasionTitle: title,
                  productName: productName,
                  productPosition: productPosition,
                  radioGroupScreenReaderDescription: descriptionLabel,
                  rootElement: rootElement,
                  shouldApplySelectionToAll: shouldApplySelectionToAll,
                  totalBaggageQuantity: totalBaggageQuantity,
                  travelerId: traveler.id
                }), isRyanairInTrip && offeringText && /*#__PURE__*/_jsx(Notice, {
                  variant: "promotional",
                  children: /*#__PURE__*/_jsx(Text, {
                    asHtml: true,
                    className: carrierTextStyles,
                    "data-testid": `ryanair-offering-text-${traveler.id}`,
                    variant: textVariants._14_BOLD,
                    children: offeringText
                  })
                })]
              })
            })]
          }), availableForPurchase && isFirstTraveler && hasMultipleNonInfantTravelers(travelers) && /*#__PURE__*/_jsx(ApplySelectionToAllCheckbox, {
            firstTravelerSelectionValue: firstTravelerSelectionValue,
            label: sameChoiceForAllLabel,
            onChangeAll: onChangeAll,
            productName: productName,
            shouldApplySelectionToAll: shouldApplySelectionToAll
          })]
        })
      })
    })
  });
};
export default CabinBaggagePrimaryFlow;
//# sourceMappingURL=CabinBaggagePrimaryFlow.js.map